import "react-datepicker/dist/react-datepicker.css";

/* eslint-disable @typescript-eslint/no-explicit-any */
import { Field, useField } from "formik";
import React, { useEffect, useState } from "react";
import DatePicker from "react-datepicker";

import { Column, Row } from "@gamedev-web/gamedev-sdk-core";

import { FieldErrorMessage, FieldHeader, FieldHeaderProps, FieldStateProps, TextField } from "../";
import { formatDateFieldValue } from "../../helpers/dateHelpers";
import { useStrings } from "../../localization/localizationHelpers";
import { TargetPlatforms, TargetPlatformsDisplayName } from "../../models/targetPlatforms";
import * as commonStyles from "../commonStyles/commonStyles.module.css";
import * as styles from "./TargetPlatformPicker.module.css";

type TargetPlatformPickerProps = Readonly<{
  name: string;
}> &
  FieldHeaderProps &
  FieldStateProps;

type TargetPlatformCheckBoxProps = Readonly<{
  text: string;
  name: string;
  optionName: string;
  optionNameValue: string;
  optionReleaseDateName: string;
  otherOptionName: string;
  disabled?: boolean;
  isRequiredField?: boolean;
}> &
  FieldStateProps;

const TargetPlatformCheckBox = (props: TargetPlatformCheckBoxProps) => {
  const {
    text,
    optionReleaseDateName,
    name,
    optionName,
    optionNameValue,
    otherOptionName,
    isSaving,
    isRequiredField,
  } = props;
  const [targetPlatformsReleaseDateTitle, otherPlatformNameTitle] = useStrings(
    "targetPlatformsReleaseDateTitle",
    "otherPlatformNameTitle"
  );
  const [field, _meta, helpers] = useField({ name: name });
  const [_nameField, _nameMeta, nameHelpers] = useField({ name: optionName });
  const [releaseDateField, _releaseDateMeta, releaseDateHelpers] = useField({
    name: optionReleaseDateName,
  });
  const [checkBoxEnabled, setCheckBoxEnabled] = useState(field.value);
  const [selectedDate, setSelectedDate] = useState(
    releaseDateField.value ? new Date(formatDateFieldValue(releaseDateField.value)) : undefined
  );
  const { setValue } = releaseDateHelpers;
  useEffect(() => {
    helpers.setValue(field.value ?? false);
    nameHelpers.setValue(optionNameValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedDate) {
      setValue(selectedDate?.toLocaleDateString("en-US"));
    }
  }, [selectedDate, setValue]);

  const onClick = (_event: any) => {
    setCheckBoxEnabled(!checkBoxEnabled);
    nameHelpers.setValue(optionNameValue);
  };

  return (
    <Row className={commonStyles.slimContainer}>
      <Column>
        <div className={styles.calendarContainer}>
          <label className={styles.option}>
            <Field
              type="checkbox"
              name={name}
              data-testid={name}
              onClick={onClick}
              disabled={isSaving}
              isRequiredField={isRequiredField}
            />
            {text}
          </label>
          {checkBoxEnabled && (
            <>
              <Row>
                <Column>
                  <Row className={commonStyles.container}>
                    <div className={styles.flex}>
                      <DatePicker
                        data-testid={releaseDateField.name}
                        disabled={isSaving}
                        selected={selectedDate}
                        className={styles.calendar}
                        id={releaseDateField.name}
                        onChange={(date: Date) => setSelectedDate(date)}
                        placeholderText={targetPlatformsReleaseDateTitle}
                      />
                    </div>
                  </Row>
                  {optionNameValue === TargetPlatforms.other && (
                    <TextField
                      name={otherOptionName}
                      title={otherPlatformNameTitle}
                      isRequiredField={true}
                      isSaving={isSaving}
                    />
                  )}
                </Column>
              </Row>

              <FieldErrorMessage fieldName={releaseDateField.name} />
            </>
          )}
        </div>
      </Column>
    </Row>
  );
};

export const TargetPlatformPicker = (props: TargetPlatformPickerProps) => {
  const { name, isSaving, isRequiredField } = props;
  const [microsoftStoreOnWindowsPlatform, other] = useStrings(
    "microsoftStoreOnWindowsPlatform",
    "other"
  );
  const getPlatformLabel = (platform: string) => {
    switch (platform) {
      case TargetPlatforms.windowsStore:
        return microsoftStoreOnWindowsPlatform;
      case TargetPlatforms.other:
        return other;
      default:
        return TargetPlatformsDisplayName[platform];
    }
  };

  return (
    <Row className={commonStyles.internalContainer}>
      <Column>
        <FieldHeader titleClassName={styles.optionTitle} {...props} />
        {Object.keys(TargetPlatforms).map((platform, index) => (
          <TargetPlatformCheckBox
            key={`${name}-${index}`}
            text={getPlatformLabel(platform)}
            name={`${name}.${index}.selected`}
            optionName={`${name}.${index}.platform`}
            optionNameValue={platform}
            optionReleaseDateName={`${name}.${index}.releaseDate`}
            otherOptionName={`${name}.${index}.otherPlatformName`}
            isSaving={isSaving}
            isRequiredField={isRequiredField}
          />
        ))}
      </Column>
    </Row>
  );
};
